import { Button } from '@/components/ui/button';
import { DynamicFieldInput } from '@/components/insurance/DynamicFieldInput';
import { DriversSection } from '@/components/insurance/DriversSection';
import { FileText, TriangleAlert, UserPlus, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Euro } from 'lucide-react';
import type { IDynamicFieldSectionsProps, IInsuranceField } from '@/@types/insurance';

export function DynamicFieldSections({
  groupedSections,
  dynamicFields,
  ungroupedFields,
  supportsDrivers,
  selectedTypeName,
  driverForm,
  form,
  orgId,
  isSubmitting,
}: IDynamicFieldSectionsProps) {
  return (
    <>
      {/* Grouped Dynamic Field Sections */}
      {groupedSections.map(section => (
        <div
          key={section.title}
          className="rounded-xl border border-border bg-card p-6 flex flex-col gap-5"
        >
          <div className="flex items-center gap-2.5">
            <section.icon className="w-5 h-5 text-lime-700 dark:text-lime-400" />
            <h2 className="text-base font-semibold text-foreground">{section.title}</h2>
          </div>
          <div className="h-px bg-border" />
          <div className={cn('grid gap-5', section.cols === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
            {section.fields.map((field: IInsuranceField) => (
              <div
                key={field.label}
                className={cn(
                  'flex flex-col gap-1.5',
                  section.fullWidthLabels?.some(
                    l => l.toLowerCase() === field.label.toLowerCase()
                  ) && 'col-span-2'
                )}
              >
                <label
                  className={cn(
                    'text-[13px] font-medium text-foreground flex items-center gap-1.5',
                    isSubmitting && 'opacity-50'
                  )}
                >
                  {field.label.toLowerCase() === 'car market price' && (
                    <Euro className="w-3.5 h-3.5" />
                  )}
                  {field.label}
                </label>
                {field.description && (
                  <p className="text-xs text-muted-foreground -mt-1">{field.description}</p>
                )}
                <DynamicFieldInput
                  field={field}
                  value={form.getFieldValue(field.label)}
                  storageIds={form.getFieldStorageIds(field.label)}
                  onChange={val => form.setFieldValue(field.label, val)}
                  onFileUpload={storageId => form.addFileToField(field.label, storageId)}
                  onFileRemove={storageId => form.removeFileFromField(field.label, storageId)}
                  orgId={orgId}
                  disabled={isSubmitting}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Drivers Section */}
      {supportsDrivers && (
        <div className="rounded-xl border border-border bg-card p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Users className="w-5 h-5 text-lime-700 dark:text-lime-400" />
              <h2 className="text-base font-semibold text-foreground">Drivers</h2>
              <span className="text-xs font-semibold text-lime-700 dark:text-lime-400 bg-lime-100 dark:bg-lime-400/10 rounded-full px-2 py-0.5">
                {driverForm.drivers.length}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={driverForm.addDriver}
              disabled={isSubmitting}
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Add Driver
            </Button>
          </div>
          <div className="h-px bg-border" />
          <DriversSection
            drivers={driverForm.drivers}
            onUpdate={driverForm.updateDriver}
            onSetFile={driverForm.setDriverFile}
            onRemoveFile={driverForm.removeDriverFile}
            onAdd={driverForm.addDriver}
            onRemove={driverForm.removeDriver}
            mainDriverIndex={driverForm.mainDriverIndex}
            onSetMainDriver={driverForm.setMainDriverIndex}
            disabled={isSubmitting}
          />
        </div>
      )}

      {/* Claims History */}
      {dynamicFields.some(f => f.label === 'Claim history') && (
        <div className="rounded-xl border border-border bg-card p-6 flex flex-col gap-5">
          <div className="flex items-center gap-2.5">
            <TriangleAlert className="w-5 h-5 text-lime-700 dark:text-lime-400" />
            <h2 className="text-base font-semibold text-foreground">Claims History</h2>
          </div>
          <div className="h-px bg-border" />
          <div className="grid grid-cols-2 gap-5">
            {dynamicFields
              .filter(f => f.label === 'Claim history')
              .map((field: IInsuranceField) => (
                <div key={field.label} className="flex flex-col gap-1.5">
                  <label
                    className={cn(
                      'text-[13px] font-medium text-foreground',
                      isSubmitting && 'opacity-50'
                    )}
                  >
                    {field.label}
                  </label>
                  <DynamicFieldInput
                    field={field}
                    value={form.getFieldValue(field.label)}
                    storageIds={form.getFieldStorageIds(field.label)}
                    onChange={val => form.setFieldValue(field.label, val)}
                    onFileUpload={storageId => form.addFileToField(field.label, storageId)}
                    onFileRemove={storageId => form.removeFileFromField(field.label, storageId)}
                    orgId={orgId}
                    disabled={isSubmitting}
                  />
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Ungrouped Fields */}
      {ungroupedFields.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 flex flex-col gap-5">
          <div className="flex items-center gap-2.5">
            <FileText className="w-5 h-5 text-lime-700 dark:text-lime-400" />
            <h2 className="text-base font-semibold text-foreground">{selectedTypeName} Fields</h2>
          </div>
          <div className="h-px bg-border" />
          <div className="grid grid-cols-2 gap-5">
            {ungroupedFields.map((field: IInsuranceField) => (
              <div key={field.label} className="flex flex-col gap-1.5">
                <label
                  className={cn(
                    'text-[13px] font-medium text-foreground',
                    isSubmitting && 'opacity-50'
                  )}
                >
                  {field.label}
                </label>
                {field.description && (
                  <p className="text-xs text-muted-foreground -mt-1">{field.description}</p>
                )}
                <DynamicFieldInput
                  field={field}
                  value={form.getFieldValue(field.label)}
                  storageIds={form.getFieldStorageIds(field.label)}
                  onChange={val => form.setFieldValue(field.label, val)}
                  onFileUpload={storageId => form.addFileToField(field.label, storageId)}
                  onFileRemove={storageId => form.removeFileFromField(field.label, storageId)}
                  orgId={orgId}
                  disabled={isSubmitting}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
